import { mapById } from '@dmp/shared/helpers';
import { omit } from 'remeda';
import { isQuestionItem } from '../../state-type-guards';
import type {
    AnswerState,
    CardState,
    LinearQuestionState,
    QuestionState,
    StoreState,
} from '../../state-types';
import { makeSurveyDirty } from '../utils/make-survey-dirty';
import { getCardAnswers } from '../answers/get-card-answers';

/** Switch card type. Currently only support Question card. */
export const switchCardType =
    (cardId: string, type: CardState['type'], isMultiSelect: boolean = false) =>
    (state: StoreState): StoreState => {
        const card = state.cards[cardId];

        if (!card) {
            throw new Error('Card not found');
        }

        // Only support Question card switching between Linear and Forked
        if (
            // same type. do nothing
            card.type === type ||
            // not a question card
            !isQuestionItem(card) ||
            // new type is not linear or forked
            (type !== 'linearQuestion' && type !== 'forkedQuestion')
        ) {
            return state;
        }

        const surveyCardIds = state.survey.cardIds;
        const cardAnswers = getCardAnswers(cardId)(state);

        const { newCard, newAnswers } = questionTypeSwitchUtil(
            card,
            cardAnswers,
            surveyCardIds
        )(type, isMultiSelect);

        return makeSurveyDirty({
            ...state,
            cards: {
                ...state.cards,
                [cardId]: newCard,
            },
            answers: {
                ...state.answers,
                ...mapById(newAnswers),
            },
        });
    };

/**
 * Merge answer info
 */
const questionTypeSwitchUtil =
    (card: QuestionState, answers: AnswerState[], surveyCardIds: string[]) =>
    (
        newType: QuestionState['type'],
        isMultiSelect: boolean = false
    ): { newAnswers: AnswerState[]; newCard: CardState } => {
        // find next card id in the list to connect to
        const nextCardId = getNextCardConnection(card.id, surveyCardIds);

        switch (newType) {
            // switch to forkedQuestion
            // - change card type
            // - remove card's `resultCard`
            // - change answer type to 'forkedAnswer'
            // - only Linear Questions can be free-form
            case 'forkedQuestion': {
                if (card.type === 'forkedQuestion') {
                    return { newCard: card, newAnswers: answers };
                }

                return {
                    newCard: {
                        ...omit(card, ['resultCard']),
                        type: newType,
                    },
                    newAnswers: answers.map((a) => {
                        if (a.type === 'forkedAnswer') {
                            return a;
                        }

                        return {
                            ...omit(a, ['isFreeForm']),
                            type: 'forkedAnswer',
                            resultCard: nextCardId,
                        };
                    }),
                };
            }

            // switch to linearQuestion
            // - change card type
            // - add card's `resultCard` link to the next card in survey
            // - change answer type to `linearAnswer` and eliminate `resultCard` key
            case 'linearQuestion': {
                if (card.type === 'linearQuestion') {
                    return { newCard: card, newAnswers: answers };
                }

                const newCard: LinearQuestionState = {
                    ...card,
                    type: newType,
                    formType: isMultiSelect ? 'multi-select' : card.formType,
                    resultCard: nextCardId,
                };

                return {
                    newCard,
                    newAnswers: answers.map((answer) => {
                        if (answer.type === 'linearAnswer') {
                            return answer;
                        }

                        return {
                            ...omit(answer, ['resultCard']),
                            type: 'linearAnswer',
                        };
                    }),
                };
            }

            // do nothing for unsupported switch types
            default:
                return {
                    newCard: card,
                    newAnswers: answers,
                };
        }
    };

/**
 * Get the next card id for connection in the survey list
 */
const getNextCardConnection = (cardId: string, cardIdList: string[]) => {
    const cardIndex = cardIdList.indexOf(cardId);
    return cardIdList[cardIndex + 1] || '';
};
